import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import restClient from '../../services/rest';
import {
  CreateMedicationRollPost,
  GetMedicationScheduleTemplateAction,
  HubOwnerStore,
  HubOwnerStoreActionTypes,
  ResetImportMedRollFormAction,
  CreateMedRollTemplate,
  GetMedicationScheduleAction,
  GetMedicationEventHistoryAction,
  GetMedicationEventImagesAction,
  RequestMedicationEventImagesAction,
  MedEventHistory,
  HubOwner,
  FetchHubOwnersSuccessAction,
  SetLoadingAction,
  UnsetLoadingAction,
  AddHubOwnerSuccessAction,
  ResetAddHubOwnerSuccessAction,
  AddHubOwnerParams,
  EditOwnerSuccessAction,
  EditOwnerReqParams,
  HubActionStatus,
  HubApiActions,
  DuplicateOwnerAction,
  SetAddingHubOwnerAction,
  OwnerContact,
  FetchOwnerContactsSuccessAction,
  MedicationList,
  MedicationStats,
  OwnerOverview,
  UpdateCareteamRoleReq,
  SetCareteamRoleLoadingAction,
  RequestOwnerSurveyInsightsAction,
  FetchOwnerSurveyInsightsSuccessAction,
  FetchOwnerSurveyInsightsFailureAction,
  OwnerSurveyInsights,
  OwnerSurveyInsightsState,
  SurveyInsightsGranularity,
  SetDeleteContactLoadingAction,
  SetOwnerContactsLoadingAction,
} from './types';

import { ImportMedicationRoll } from '../../generated/models/ImportMedicationRoll';
import {
  MedEventType,
  MedicationDoseEventType,
  MedEventStatusEnum,
  LoadScheduleMatchItem,
  MedEventImages,
  UpdateMedEvents,
} from '../../generated';
import axios from 'axios';
import { setLoadState } from '../customers/actionCreators';
import {
  Drug,
  MedEvent,
  Pouch,
} from '../../components/Shared/ImportMedicationRollStepFormTypes';
import _, { maxBy as _maxBy } from 'lodash';
import { RelationshipToOwner } from '../../shared/constants';
import { restHost } from '../../apiConfig';
import { formatUrl, getApiErrorMessage, getCookie } from '../../shared/utils';

// The generated rest client throws ApiError, which carries the parsed
// response body on `body` (an object with `message`, or a bare string).
function createMedicationRollSuccess() {
  return {
    type: HubOwnerStoreActionTypes.CREATE_MEDICATION_ROLL_SUCCESS,
  };
}

function getMedicationScheduleTemplateSuccess(
  data: CreateMedRollTemplate,
): GetMedicationScheduleTemplateAction {
  return {
    type: HubOwnerStoreActionTypes.GET_MEDICATION_SCHEDULE_TEMPLATE,
    payload: { medScheduleTemplate: data },
  };
}

function getMedicationScheduleSuccess(data: {
  medSchedule: LoadScheduleMatchItem[];
  schedule_local_timezone?: string;
  med_window_time_sec?: number;
}): GetMedicationScheduleAction {
  return {
    type: HubOwnerStoreActionTypes.GET_MEDICATION_SCHEDULE,
    payload: {
      medSchedule: data.medSchedule,
      schedule_local_timezone: data.schedule_local_timezone,
      med_window_time_sec: data.med_window_time_sec,
    },
  };
}

function getMedicationEventHistorySuccess(
  ownerId: number,
  medEventId: string,
  data: MedEventHistory,
): GetMedicationEventHistoryAction {
  return {
    type: HubOwnerStoreActionTypes.GET_MEDICATION_EVENT_HISTORY,
    payload: { ownerId, medEventId, medEventHistory: data },
  };
}

function requestMedicationEventImages(
  requestId: number,
): RequestMedicationEventImagesAction {
  return {
    type: HubOwnerStoreActionTypes.REQUEST_MEDICATION_EVENT_IMAGES,
    payload: { requestId },
  };
}

function getMedicationEventImagesSuccess(
  ownerId: number,
  medEventId: string,
  data: MedEventImages,
  requestId: number,
): GetMedicationEventImagesAction {
  return {
    type: HubOwnerStoreActionTypes.GET_MEDICATION_EVENT_IMAGES,
    payload: { ownerId, medEventId, medEventImages: data, requestId },
  };
}

export const resetImportMedRollForm = (): ResetImportMedRollFormAction => {
  return {
    type: HubOwnerStoreActionTypes.RESET_IMPORT_MED_ROLL_FORM,
  };
};

export const setLoading = (isLoading: boolean): SetLoadingAction => {
  return {
    type: HubOwnerStoreActionTypes.SET_LOADING,
    payload: isLoading,
  };
};

export const unsetLoading = (): UnsetLoadingAction => {
  return {
    type: HubOwnerStoreActionTypes.UNSET_LOADING,
  };
};

export const addHubOwnerSuccess = (
  message: string,
): AddHubOwnerSuccessAction => {
  return {
    type: HubOwnerStoreActionTypes.ADD_HUB_OWNER_SUCCESS,
    payload: { message },
  };
};

export const resetAddHubOwnerSuccess = (): ResetAddHubOwnerSuccessAction => {
  return {
    type: HubOwnerStoreActionTypes.RESET_ADD_HUB_OWNER_SUCCESS,
  };
};

export const setActionStatus = (status: Partial<HubActionStatus>) => ({
  type: HubOwnerStoreActionTypes.SET_ACTION_STATUS,
  payload: status,
});

export const clearHubActionStatus = () => ({
  type: HubOwnerStoreActionTypes.CLEAR_ACTION_STATUS,
});

export const setAddingHubOwner = (
  isAdding: boolean,
): SetAddingHubOwnerAction => ({
  type: HubOwnerStoreActionTypes.SET_ADDING_HUB_OWNER,
  payload: isAdding,
});

function fetchOwnerContactsSuccess(
  data: OwnerContact[],
): FetchOwnerContactsSuccessAction {
  return {
    type: HubOwnerStoreActionTypes.FETCH_OWNER_CONTACTS_SUCCESS,
    payload: data,
  };
}

export const fetchMedicationListSuccess = (
  ownerId: number,
  medications: MedicationList,
  stats: MedicationStats | null = null,
) => ({
  type: HubOwnerStoreActionTypes.FETCH_MEDICATION_LIST_SUCCESS,
  payload: { ownerId, medications, stats },
});

export const setOwnerOverviewLoading = (isLoading: boolean) => ({
  type: HubOwnerStoreActionTypes.SET_OWNER_OVERVIEW_LOADING,
  payload: isLoading,
});

export const setOwnerContactsLoading = (
  isLoading: boolean,
): SetOwnerContactsLoadingAction => ({
  type: HubOwnerStoreActionTypes.SET_OWNER_CONTACTS_LOADING,
  payload: isLoading,
});

export const fetchOwnerOverviewSuccess = (data: OwnerOverview) => ({
  type: HubOwnerStoreActionTypes.FETCH_OWNER_OVERVIEW_SUCCESS,
  payload: data,
});

export const requestOwnerSurveyInsights = (
  ownerId: number,
  requestedDays: number,
  granularity: SurveyInsightsGranularity,
  requestId: number,
): RequestOwnerSurveyInsightsAction => ({
  type: HubOwnerStoreActionTypes.REQUEST_OWNER_SURVEY_INSIGHTS,
  payload: { ownerId, requestedDays, granularity, requestId },
});

export const fetchOwnerSurveyInsightsSuccess = (
  insights: OwnerSurveyInsightsState,
  requestId: number,
): FetchOwnerSurveyInsightsSuccessAction => ({
  type: HubOwnerStoreActionTypes.FETCH_OWNER_SURVEY_INSIGHTS_SUCCESS,
  payload: { ...insights, requestId },
});

export const fetchOwnerSurveyInsightsFailure = (
  requestId: number,
  error: string,
): FetchOwnerSurveyInsightsFailureAction => ({
  type: HubOwnerStoreActionTypes.FETCH_OWNER_SURVEY_INSIGHTS_FAILURE,
  payload: { requestId, error },
});

export const setCareteamRoleLoading = (
  isLoading: boolean,
): SetCareteamRoleLoadingAction => ({
  type: HubOwnerStoreActionTypes.SET_CARETEAM_ROLE_LOADING,
  payload: isLoading,
});

export const setDeleteContactLoading = (
  isLoading: boolean,
): SetDeleteContactLoadingAction => ({
  type: HubOwnerStoreActionTypes.SET_DELETE_CONTACT_LOADING,
  payload: isLoading,
});

export const addHubOwner = (data: AddHubOwnerParams) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setAddingHubOwner(true));
    try {
      const response = await restClient.owners.addNewOwner(data);
      dispatch(addHubOwnerSuccess('Hub owner added successfully'));
      dispatch(
        setActionStatus({
          success: true,
          data: response.new_owner_id,
          apiAction: HubApiActions.ADD_HUB_OWNER,
          errorAction: null,
        }),
      );
      toast.success('Hub owner added successfully');
    } catch (err: any) {
      let errorMessage = 'Something went wrong';
      let errorAction: DuplicateOwnerAction | null = null;
      let showToast = true;
      if (err.status) {
        switch (err.status) {
          case 409:
            // Duplicate check can return { action } instead of the plain
            // "Customer already exists" string
            if (err.body?.action === 'get_phone') {
              errorAction = 'get_phone';
              // Keep the message vague — it must not reveal that a matching
              // patient record exists (inference/enumeration risk)
              errorMessage = 'Phone number required';
              // Form re-opens with an inline banner; a toast would be noise
              showToast = false;
            } else if (err.body?.action === 'contact_cc') {
              errorAction = 'contact_cc';
              errorMessage = 'Something went wrong';
            } else {
              errorMessage = 'Customer already exists';
            }
            break;
          case 500:
            errorMessage = 'Cannot add owner. Please try again';
            break;
        }
      }
      dispatch(
        setActionStatus({
          success: false,
          error: errorMessage,
          apiAction: HubApiActions.ADD_HUB_OWNER,
          errorAction,
        }),
      );
      if (showToast) {
        toast.error(errorMessage);
      }
    } finally {
      dispatch(setAddingHubOwner(false));
    }
  };
};

function fetchHubOwnersSuccess(data: HubOwner[]): FetchHubOwnersSuccessAction {
  return {
    type: HubOwnerStoreActionTypes.FETCH_HUB_OWNERS_SUCCESS,
    payload: data,
  };
}

export const fetchHubOwners = (
  relationshipToOwner?: RelationshipToOwner,
  soleRelationship?: boolean,
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await restClient.owners.getOwners(
        relationshipToOwner,
        soleRelationship,
      );
      dispatch(fetchHubOwnersSuccess(response));
    } catch (err: any) {
      toast.error(err?.response?.data || err);
    } finally {
      dispatch(unsetLoading());
    }
  };
};

export const editOwnerSuccess = (
  ownerId: number,
  requestBody: EditOwnerReqParams,
): EditOwnerSuccessAction => {
  return {
    type: HubOwnerStoreActionTypes.EDIT_OWNER_SUCCESS,
    payload: { ownerId, requestBody },
  };
};

export const editOwner = (ownerId: number, requestBody: EditOwnerReqParams) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoading(true));
    try {
      await restClient.owners.editOwner(ownerId, requestBody);
      dispatch(editOwnerSuccess(ownerId, requestBody));
      toast.success('Owner details updated successfully');
      return Promise.resolve();
    } catch (err: any) {
      return Promise.reject(err);
    } finally {
      dispatch(unsetLoading());
    }
  };
};

export const createMedicationRoll = (data: CreateMedicationRollPost) => {
  const params = {
    batch_id: data.batchId,
    start_date: data.startDate.toFormat('yyyy-MM-dd'),
    pharmacy_id: data.pharmacyId,
    schedule_template_cadence: 'REPEAT_DAYS',
    schedule_template_duration: data.scheduleTemplateDuration,
    schedule_template: data.scheduleTemplate.map((medEvent) => {
      return {
        prescribed_time_of_day_24:
          medEvent.prescribedTimeOfDay24.toLocaleString(
            DateTime.TIME_24_SIMPLE,
          ),
        desired_time_of_day_24: medEvent.desiredTimeOfDay24.toLocaleString(
          DateTime.TIME_24_SIMPLE,
        ),
        event_type: medEvent.eventType,
        pouches: medEvent.pouches.map((pouch) => {
          return {
            pouch_number: pouch.pouchNumber,
            drugs: pouch.drugs.map((d) => {
              return {
                national_drug_code: d.nationalDrugCode,
                roll_packaged: d.rollPackaged,
                is_prn: d.isPrn,
              };
            }),
          };
        }),
      };
    }),
  } as ImportMedicationRoll;

  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      await restClient.owners.createMedicationRoll(data.customerId, params);

      toast.success('Successfully imported medication roll');
      dispatch(createMedicationRollSuccess());
    } catch (err: any) {
      toast.error(err?.response?.data ?? '');
    }
  };
};

export const getMedicationSchedule = (
  customerId: number,
  startDate: luxon.DateTime = DateTime.fromObject({
    day: 1,
    year: 1970,
    month: 1,
  }),
  endDate: luxon.DateTime = DateTime.fromJSDate(new Date(86400000000000)),
  includeInvalidated: boolean = true,
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      dispatch(setLoadState(true));
      const medSchedule = await restClient.owners.getMedScheduleByOwnerId(
        customerId,
        startDate.toISODate(),
        endDate.toISODate(),
        includeInvalidated,
      );

      const simplifiedMedSchedule = (
        medSchedule?.schedule
          ? medSchedule?.schedule.map((medEvent) => {
              const doses = medEvent.pouches
                .flatMap((pouch) =>
                  pouch.doses.map((dose) => ({
                    ...dose,
                    ...{
                      batch_id: pouch.batch_id.toString(),
                      pouch_number: pouch.pouch_number,
                    },
                  })),
                )
                .map((dose) =>
                  _.assign(
                    {},
                    _.omit(dose, [
                      'dose_id',
                      'medication_dose_status_id',
                      'roll_packaged',
                      'received_at',
                      'administered_timezone',
                      'tablet_count',
                    ]),
                    {
                      id: dose.dose_id,
                      status_id: dose.medication_dose_status_id,
                      roll_packaged: dose.roll_packaged === true,
                      dosage: dose.tablet_count
                        ? `QTY ${dose.tablet_count}`
                        : '',
                      consumption_notes: ' ',
                    },
                  ),
                );

              let med_event_status;
              const counts = _.countBy(doses, 'status');
              if (counts.active) {
                med_event_status = MedEventStatusEnum.ACTIVE;
              }
              if (!counts.active && counts.complete) {
                med_event_status = MedEventStatusEnum.COMPLETE;
              }
              if (!counts.complete && !counts.active) {
                if (counts.missed) {
                  med_event_status = MedEventStatusEnum.MISSED;
                }
                if (counts.upcoming) {
                  med_event_status = MedEventStatusEnum.UPCOMING;
                }
                if (!counts.missed && !counts.upcoming && counts.skipped) {
                  med_event_status = MedEventStatusEnum.COMPLETE;
                }
              }

              return _.assign(
                {},
                _.omit(medEvent, ['pouches', 'dose_count', 'pouch_count']),
                {
                  status: med_event_status,
                  doses,
                },
              );
            })
          : []
      ) as LoadScheduleMatchItem[];

      dispatch(
        getMedicationScheduleSuccess({
          medSchedule: simplifiedMedSchedule,
          schedule_local_timezone: medSchedule?.schedule_local_timezone,
          med_window_time_sec: (medSchedule as any)?.med_window_time_sec,
        }),
      );
      dispatch(setLoadState(false));
    } catch (err: any) {
      dispatch(setLoadState(false));
      toast.error(err?.response?.data || err);
    }
  };
};

/**
 * Seam-detection dispense images for one med event (OI-1368). Presigned URLs
 * expire after `url_expires_in_seconds`, so the tab re-fetches on every open
 * instead of trusting a cached copy.
 * Rethrows after toasting so the tab can show an error state instead of an
 * empty "No data" card (the tab only exists when the API said images exist).
 */
let medEventImagesRequestSeq = 0;

export const getMedicationEventImages = (
  ownerId: number,
  medEventId: string,
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    // Responses can land out of request order (popup re-anchored from event A
    // to B while A is in flight). Tag each request so the reducer keeps only
    // the latest one and a slow, superseded response can't clobber the store.
    const requestId = ++medEventImagesRequestSeq;
    dispatch(requestMedicationEventImages(requestId));
    try {
      const images =
        await restClient.owners.getMedEventImagesByOwnerIdAndMedEventId(
          ownerId,
          medEventId,
        );
      dispatch(
        getMedicationEventImagesSuccess(ownerId, medEventId, images, requestId),
      );
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Could not load medication images'));
      throw err;
    }
  };
};

export const getMedicationEventHistory = (
  ownerId: number,
  medEventId: string,
  withGlobalLoading: boolean = true, // Optional flag to control redux loading
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      if (withGlobalLoading) {
        dispatch(setLoadState(true));
      }
      const history =
        await restClient.owners.getMedEventHistoryByOwnerIdAndMedEventId(
          ownerId,
          medEventId,
        );
      dispatch(
        getMedicationEventHistorySuccess(
          ownerId,
          medEventId,
          history as MedEventHistory,
        ),
      );
      dispatch(setLoadState(false));
    } catch (err: any) {
      toast.error(
        getApiErrorMessage(err, 'Could not load medication event history'),
      );
    }
  };
};

export const addMedicationDoseEvents = (
  ownerId: number,
  eventName: MedicationDoseEventType,
  requestBody: {
    /**
     * The timestamp of the event.
     */
    timestamp: string;
    /**
     * An array of doses.
     */
    doses: Array<{
      /**
       * The ID of the dose.
       */
      dose_id: string;
      /**
       * The flow of dispensing, this is only required when event_name is dispensed
       */
      dispensed_flow?: 'past_due' | 'on_the_go' | 'med_assist';
    }>;
  },
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      dispatch(setLoadState(true));
      await restClient.owners.addMedicationDoseEvents(
        ownerId,
        eventName,
        requestBody,
      );

      toast.success('Medication schedule updated');
      dispatch(getMedicationSchedule(ownerId));
    } catch (err: any) {
      toast.error('Failed to update medication schedule');
    }
  };
};

export const deleteOwnersMedicationDoses = (
  ownerId: number,
  requestBody: {
    /**
     * Filter conditions to determine which medication events to delete
     */
    filters: {
      /**
       * Array of batch IDs to be deleted
       */
      batch_ids?: Array<string>;
      /**
       * Array of pouch numbers to target specific medication events
       */
      pouch_numbers?: Array<number>;
      /**
       * Array of dose IDs to specify which doses to delete
       */
      dose_ids?: Array<number>;
      /**
       * Array of medication event IDs for deletion in the 'YYYY-MM-DD HH:mm:ss' format
       */
      med_event_ids?: Array<string>;
    };
    /**
     * A flag indicating whether only medication event history should be deleted
     */
    delete_history_only?: boolean;
  },
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      dispatch(setLoadState(true));
      await restClient.owners.deleteOwnersMedicationDoses(ownerId, requestBody);

      toast.success('Medication events/history deleted');
      dispatch(getMedicationSchedule(ownerId));
    } catch (err: any) {
      toast.error('Failed to delete medication events/history');
    }
  };
};

export const updateMedEvents = (
  ownerId: number,
  requestBody: UpdateMedEvents,
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      dispatch(setLoadState(true));
      await restClient.owners.updateMedEvents(ownerId, requestBody);

      toast.success('Medication events updated');
      dispatch(getMedicationSchedule(ownerId));
    } catch (err: any) {
      toast.error('Failed to update medication events');
    }
  };
};

export const updateSingleMedEvent = (
  ownerId: number,
  requestBody: {
    med_event_id: string;
    desired_admin_time: string;
    event_type: 'CAREGIVER_ASSIST' | 'SMART_ASSIST';
    caregiver_customer_id?: number;
  },
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      dispatch(setLoadState(true));
      await restClient.owners.updateMedScheduleByOwnerId(ownerId, requestBody);
      toast.success('Medication event updated');
      dispatch(getMedicationSchedule(ownerId));
    } catch (err: any) {
      toast.error('Failed to update medication event');
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getMedicationScheduleTemplate = (customerId: number) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      const medScheduleTemplate =
        await restClient.owners.getMedScheduleByOwnerId(
          customerId,
          DateTime.fromObject({ day: 1, year: 1970, month: 1 }).toISODate(),
          DateTime.fromJSDate(new Date(86400000000000)).toISODate(),
          true,
        );

      const medRoll = _maxBy(medScheduleTemplate?.med_rolls, 'end_datetime');

      if (!medRoll) {
        return;
      }

      const matchingMedEvents = _.filter(
        medScheduleTemplate?.schedule,
        (x) =>
          DateTime.fromISO(x.prescribed_datetime).toISODate() ===
            DateTime.fromISO(medRoll.end_datetime).toISODate() &&
          _.some(x.pouches, (p) => p.batch_id === medRoll.batch_id),
      );

      const medEvents = matchingMedEvents?.map((x) => {
        return {
          desiredTimeOfDay: DateTime.fromISO(x.desired_datetime, {
            zone: medScheduleTemplate.schedule_local_timezone,
          }),
          eventType: MedEventType[x.event_type],
          pouches: x.pouches
            ?.filter((p) => p.batch_id === medRoll.batch_id)
            .map((p) => {
              return {
                pouchNumber: p.pouch_number?.toString(),
                drugs: p.doses?.map((d) => {
                  return {
                    name: d.drug_name,
                    ndc: d.national_drug_code,
                    rollPackaged: d.roll_packaged,
                    isPrn: d.is_prn,
                  };
                }) as Drug[],
              };
            }) as Pouch[],
        };
      }) as MedEvent[];
      dispatch(
        getMedicationScheduleTemplateSuccess({
          medEvents: medEvents,
          batchId: medRoll.batch_id,
          date: medEvents.length
            ? DateTime.fromISO(medRoll.end_datetime, {
                zone: medScheduleTemplate.schedule_local_timezone,
              }).startOf('day')
            : undefined,
        }),
      );
    } catch (err: any) {
      toast.error(err?.response?.data || err);
    }
  };
};

export const fetchOwnerContacts = (
  ownerId: number,
  caregiverAssistPermission?: boolean,
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setOwnerContactsLoading(true));
    try {
      const response = await restClient.owners.getOwnerContacts(
        ownerId,
        caregiverAssistPermission,
      );
      dispatch(fetchOwnerContactsSuccess(response));
    } catch (err: any) {
      toast.error(err?.response?.data || err);
    } finally {
      dispatch(setOwnerContactsLoading(false));
    }
  };
};

export const fetchMedicationList = (ownerId: number) => {
  return async (dispatch: any) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `owners/${ownerId}/medication`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      // OI-1640 changed this response from a bare array to { medications, stats }.
      // Tolerate both shapes so the list keeps working across the backend rollout;
      // `stats` (OI-1320) is only present on the new shape.
      const medications = Array.isArray(response.data)
        ? response.data
        : (response.data?.medications ?? []);
      const stats: MedicationStats | null = Array.isArray(response.data)
        ? null
        : (response.data?.stats ?? null);
      dispatch(fetchMedicationListSuccess(ownerId, medications, stats));
    } catch (err: any) {
      toast.error(
        err?.response?.data || err.message || 'Failed to fetch medication list',
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const fetchOwnerOverview = (ownerId: number) => {
  return async (dispatch: any) => {
    dispatch(setOwnerOverviewLoading(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `owners/${ownerId}/overview`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(fetchOwnerOverviewSuccess({ ...response.data, ownerId }));
    } catch (err: any) {
      toast.error(
        err?.response?.data || err.message || 'Failed to fetch owner overview',
      );
    } finally {
      dispatch(setOwnerOverviewLoading(false));
    }
  };
};

// Patient Insights tab: GET /owners/:id/survey_insights over a rolling
// window of `days` ending today (all survey types in one call), bucketed by
// `granularity`. Each request carries a sequence id; the reducer drops
// responses that are no longer the latest so switching patient or window
// mid-flight cannot show stale data.
let surveyInsightsRequestSeq = 0;

// `timeZone` is the viewer's IANA zone (getBrowserTimeZone): the server cuts
// the window and its day buckets on it, so they line up with the local times
// the client shows.
export const fetchOwnerSurveyInsights = (
  ownerId: number,
  days: number,
  granularity: SurveyInsightsGranularity,
  timeZone: string,
) => {
  return async (dispatch: any) => {
    const requestId = ++surveyInsightsRequestSeq;
    dispatch(requestOwnerSurveyInsights(ownerId, days, granularity, requestId));
    try {
      const response = await restClient.owners.getOwnerSurveyInsights(
        ownerId,
        days,
        granularity,
        undefined,
        timeZone,
      );
      dispatch(
        fetchOwnerSurveyInsightsSuccess(
          {
            ...(response as OwnerSurveyInsights),
            ownerId,
            requestedDays: days,
          },
          requestId,
        ),
      );
    } catch (err: any) {
      const message = getApiErrorMessage(
        err,
        'Failed to fetch survey insights',
      );
      toast.error(message);
      dispatch(fetchOwnerSurveyInsightsFailure(requestId, message));
    }
  };
};

export const updateCareteamRole = (
  ownerId: number,
  customerId: number,
  req: UpdateCareteamRoleReq,
) => {
  return async (
    dispatch: ThunkDispatch<HubOwnerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setCareteamRoleLoading(true));
    try {
      const response = await axios.put(
        formatUrl(restHost, `owners/${ownerId}/careteam/${customerId}`),
        req,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.status === 200) {
        dispatch(
          setActionStatus({
            apiAction: HubApiActions.UPDATE_CARETEAM_ROLE,
            success: true,
            error: null,
          }),
        );
      } else {
        toast.error(`Unexpected response status: ${response.status}`, {
          autoClose: 3000,
        });
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data || err.message || 'Failed to update careteam role',
        {
          autoClose: 3000,
        },
      );
    } finally {
      dispatch(setCareteamRoleLoading(false));
    }
  };
};

export const deleteOwnerContact = (
  ownerId: number,
  contact: { customer_id?: number; status?: string; email?: string },
) => {
  return async (dispatch: any) => {
    dispatch(setDeleteContactLoading(true));
    try {
      await restClient.owners.deleteOwnerContacts(ownerId, {
        customer_id: contact.customer_id,
        status: contact.status,
        email: contact.email,
      } as any);
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.DELETE_OWNER_CONTACT,
          success: true,
          error: null,
        }),
      );
    } catch (err: any) {
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.DELETE_OWNER_CONTACT,
          success: false,
          error: err?.response?.data || 'Failed to delete user',
        }),
      );
    } finally {
      dispatch(setDeleteContactLoading(false));
    }
  };
};

export const createCareteamInvite = (
  customerId: number,
  invite: {
    first_name: string;
    last_name: string;
    email: string;
    careteam_role_id: number;
  },
) => {
  return async (dispatch: any) => {
    dispatch(setLoadState(true));
    try {
      await axios.post(
        formatUrl(restHost, `customer/${customerId}/careteam/invites`),
        invite,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.CREATE_CARETEAM_INVITE,
          success: true,
          error: null,
        }),
      );
    } catch (err: any) {
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.CREATE_CARETEAM_INVITE,
          success: false,
          error: err?.response?.data || 'Failed to send invite',
        }),
      );
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const deleteCareteamInvite = (customerId: number, email: string) => {
  return async (dispatch: any) => {
    dispatch(setDeleteContactLoading(true));
    try {
      await axios.delete(
        formatUrl(restHost, `customer/${customerId}/careteam/invites/${email}`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.DELETE_CARETEAM_INVITE,
          success: true,
          error: null,
        }),
      );
    } catch (err: any) {
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.DELETE_CARETEAM_INVITE,
          success: false,
          error: err?.response?.data || 'Failed to delete invite',
        }),
      );
    } finally {
      dispatch(setDeleteContactLoading(false));
    }
  };
};

export const resendCareteamInvite = (
  customerId: number,
  invite: {
    first_name: string;
    last_name: string;
    email: string;
    careteam_role_id: number;
  },
) => {
  return async (dispatch: any) => {
    try {
      await axios.post(
        formatUrl(restHost, `customer/${customerId}/careteam/invites`),
        invite,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.RESEND_CARETEAM_INVITE,
          success: true,
          error: null,
        }),
      );
    } catch (err: any) {
      dispatch(
        setActionStatus({
          apiAction: HubApiActions.RESEND_CARETEAM_INVITE,
          success: false,
          error: err?.response?.data || 'Failed to resend invite',
        }),
      );
    }
  };
};
